import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { Icon } from '../components/Icon';
import { type Quote, orderApi, quoteApi } from '../lib/api';

export const Route = createFileRoute('/quotes/$quoteId')({
  component: QuoteDetailPage,
});

function fmt(cents: number, cur: string) {
  const amount = cents / 100;
  if (cur === 'JPY') return `¥${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `${cur === 'USD' ? '$' : cur === 'EUR' ? '€' : cur} ${amount.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

function QuoteDetailPage() {
  const { quoteId } = useParams({ from: '/quotes/$quoteId' });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: quote, isLoading } = useQuery({
    queryKey: ['quote', quoteId],
    queryFn: () => quoteApi.get(quoteId),
  });

  const updateMutation = useMutation({
    mutationFn: (patch: Partial<Quote>) => quoteApi.update(quoteId, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote', quoteId] });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
    },
  });

  // v1.37.9: 报价 → 订单 (补上核心业务链路断掉的一环)
  //   之前只有 orders 页的手动新建表单, 必须手抄金额和贸易术语,
  //   而且 order.quoteId 永远为 NULL → pipeline 的 avgDaysToOrder 恒为 null。
  const [convertError, setConvertError] = useState<string | null>(null);
  const convertMutation = useMutation({
    mutationFn: () => {
      if (!quote) throw new Error('报价单未加载');
      return orderApi.create({
        customerId: quote.customerId,
        quoteId: quote.id,
        incoterm: quote.incoterm,
        incotermPlace: quote.incotermPlace,
        totalAmountCents: quote.selectedTotalCents,
        currency: quote.currency,
        notes: `由报价单 ${quote.quoteNumber} 转入`,
      });
    },
    onSuccess: (order) => {
      setConvertError(null);
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      navigate({ to: '/orders/$orderId', params: { orderId: order.id } });
    },
    onError: (e) => setConvertError((e as Error).message),
  });

  if (isLoading) return <div className="text-ink-3 text-sm">加载中...</div>;
  if (!quote) return <div>未找到</div>;

  const prices: { key: keyof typeof quote; label: string; cents: number | undefined }[] = [
    { key: 'exwTotalCents', label: 'EXW', cents: quote.exwTotalCents },
    { key: 'fobTotalCents', label: 'FOB', cents: quote.fobTotalCents },
    { key: 'cfrTotalCents', label: 'CFR', cents: quote.cfrTotalCents },
    { key: 'cifTotalCents', label: 'CIF', cents: quote.cifTotalCents },
    { key: 'dapTotalCents', label: 'DAP', cents: quote.dapTotalCents },
    { key: 'ddpTotalCents', label: 'DDP', cents: quote.ddpTotalCents },
  ];

  return (
    <div className="max-w-4xl">
      <Link to="/quotes" className="text-sm text-accent inline-block mb-3">
        ← 返回报价列表
      </Link>

      <div className="bg-surface border border-line rounded p-6 mb-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold">{quote.quoteNumber}</h1>
            <p className="text-sm text-ink-2 mt-1">
              {quote.incoterm} {quote.incotermPlace} · 货币 {quote.currency}
            </p>
          </div>
          <div className="flex gap-2">
            {quote.status === 'draft' && (
              <>
                <button
                  type="button"
                  onClick={() => updateMutation.mutate({ status: 'sent' })}
                  className="px-3 py-1.5 bg-accent text-white rounded text-sm hover:bg-accent-2"
                >
                  标记已发
                </button>
                <button
                  type="button"
                  onClick={() => updateMutation.mutate({ status: 'rejected' })}
                  className="px-3 py-1.5 border border-line rounded text-sm hover:bg-surface-2"
                >
                  关闭
                </button>
              </>
            )}
            {quote.status === 'sent' && (
              <>
                <button
                  type="button"
                  onClick={() => updateMutation.mutate({ status: 'accepted' })}
                  className="px-3 py-1.5 bg-good text-white rounded text-sm"
                >
                  客户接受
                </button>
                <button
                  type="button"
                  onClick={() => updateMutation.mutate({ status: 'rejected' })}
                  className="px-3 py-1.5 border border-line rounded text-sm hover:bg-surface-2"
                >
                  客户拒绝
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
          <div>
            <div className="text-xs text-ink-3 mb-1">付款条件</div>
            <div>{quote.paymentTerms}</div>
          </div>
          <div>
            <div className="text-xs text-ink-3 mb-1">有效期至</div>
            <div>{new Date(quote.validUntil).toLocaleDateString('zh-CN')}</div>
          </div>
        </div>

        <h2 className="text-sm font-semibold mb-2">6 种贸易形式价格</h2>
        <table className="w-full text-sm">
          <tbody>
            {prices.map((p) => (
              <tr key={p.key} className="border-b border-line last:border-0">
                <td className="py-2 text-ink-2">{p.label}</td>
                <td className="py-2 text-right font-medium">
                  {p.cents !== undefined && p.cents !== null ? fmt(p.cents, quote.currency) : '—'}
                </td>
                <td className="py-2 text-right text-xs">
                  {p.key === 'selectedTotalCents' || p.label === quote.incoterm ? (
                    <span className="tag tag-good">客户看到</span>
                  ) : null}
                </td>
              </tr>
            ))}
            <tr className="bg-accent-soft">
              <td className="py-2 px-2 font-semibold">选中（{quote.incoterm}）</td>
              <td className="py-2 text-right font-semibold text-accent-2">
                {fmt(quote.selectedTotalCents, quote.currency)}
              </td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      <div className="bg-surface border border-line rounded p-5 mb-4">
        <h2 className="text-sm font-semibold mb-3">报价参数快照</h2>
        {quote.quoteParams ? (
          <table className="w-full text-xs text-ink-2">
            <tbody>
              <tr>
                <td className="py-1">EXW 单价</td>
                <td className="text-right">
                  {(quote.quoteParams.exwUnitPriceCents / 100).toFixed(2)} USD
                </td>
              </tr>
              <tr>
                <td className="py-1">数量</td>
                <td className="text-right">{quote.quoteParams.qty} pcs</td>
              </tr>
              <tr>
                <td className="py-1">总体积</td>
                <td className="text-right">{quote.quoteParams.totalCbm} CBM</td>
              </tr>
              <tr>
                <td className="py-1">海运 USD/CBM</td>
                <td className="text-right">
                  {(quote.quoteParams.freightRateUsdCentsPerCbm / 100).toFixed(2)}
                </td>
              </tr>
              <tr>
                <td className="py-1">保险费率</td>
                <td className="text-right">
                  {(quote.quoteParams.insuranceRate * 100).toFixed(2)}%
                </td>
              </tr>
              <tr>
                <td className="py-1">目的国关税</td>
                <td className="text-right">
                  {(quote.quoteParams.destCustomsDutyRate * 100).toFixed(2)}%
                </td>
              </tr>
              <tr>
                <td className="py-1">目的国 VAT</td>
                <td className="text-right">{(quote.quoteParams.destVatRate * 100).toFixed(2)}%</td>
              </tr>
            </tbody>
          </table>
        ) : null}
      </div>

      <div className="bg-surface border border-line rounded p-5">
        <h2 className="text-sm font-semibold mb-3">下一步操作</h2>
        {/* v1.37.9: 审计发现这两个按钮从 v1.5 起一直是 disabled 占位,
            但后端 GET /api/quotes/:id/pdf 早就实现了 — 死按钮。
            现在: ① 下载 PI PDF 走真实端点  ② 加「客户接受 → 转订单」
            转订单是补上「询盘→报价→订单」链路断掉的最后一环:
            之前 order.quoteId 永远为 NULL, pipeline 的「平均询盘至下单天数」恒为 null。 */}
        <div className="flex flex-wrap gap-2 text-sm">
          <a
            href={`/api/quotes/${quoteId}/pdf`}
            target="_blank"
            rel="noreferrer"
            className="px-3 py-1.5 border border-line rounded hover:bg-surface-2 inline-flex items-center gap-1 min-h-[36px]"
          >
            <Icon name="file-text" size="xs" strokeWidth={2} />
            下载 PI PDF
          </a>
          <button
            type="button"
            onClick={() => convertMutation.mutate()}
            disabled={convertMutation.isPending || quote.status !== 'accepted'}
            className="px-3 py-1.5 border border-line rounded hover:bg-surface-2 inline-flex items-center gap-1 min-h-[36px] disabled:opacity-50"
            title={
              quote.status === 'accepted'
                ? '按这张报价单建订单 (prefill 客户/贸易术语/金额)'
                : '先把报价状态改成「客户已接受」才能转订单'
            }
          >
            <Icon name="list" size="xs" strokeWidth={2} />
            {convertMutation.isPending ? '转换中…' : '转订单'}
          </button>
        </div>
        {quote.status !== 'accepted' && (
          <p className="text-xs text-ink-3 mt-2">
            客户接受报价后把状态改成「已接受」, 这里就能一键转订单 (自动带上客户 / 贸易术语 /
            金额)。
          </p>
        )}
        {convertMutation.isError && (
          <p className="text-xs text-danger mt-2">
            转订单失败: {(convertMutation.error as Error).message}
          </p>
        )}
      </div>
    </div>
  );
}
